import { useImperativeHandle, useRef, useState } from 'react';
import type { FormEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { NgButton, NgDataListSelect, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import type { IncidentPsap, Position } from '@/signalr';
import { requestCollaboration } from '../actions/emsCommands';
import {
  ADD_PSAP_TO_INCIDENT_CONFIGURATION_QUERY_KEY,
  fetchAddPsapToIncidentConfiguration,
} from '../api/portalIncidentApi';
import {
  getPsapDisplayName,
  notAlreadyInvolvedPsaps,
  orderRelatedPsaps,
} from './addPsapToIncidentModel';
import type { PsapWithDistance } from './addPsapToIncidentModel';

const FORM_ID = 'add-psap-to-incident-form';

export type AddPsapToIncidentDialogHandle = {
  showModal: () => void;
};

export type AddPsapToIncidentDialogProps = {
  incidentId: string;
  lastCallerPosition: Position | null;
  involvedPsaps?: readonly IncidentPsap[];
  ref?: Ref<AddPsapToIncidentDialogHandle>;
};

export function AddPsapToIncidentDialog({
  incidentId,
  lastCallerPosition,
  involvedPsaps,
  ref,
}: AddPsapToIncidentDialogProps) {
  const { t, i18n } = useTranslation();
  const modalRef = useRef<NgModalHandle>(null);

  const configurationQuery = useQuery({
    queryKey: ADD_PSAP_TO_INCIDENT_CONFIGURATION_QUERY_KEY,
    queryFn: fetchAddPsapToIncidentConfiguration,
  });
  const relatedPsaps = configurationQuery.data?.relatedPsaps ?? [];

  const [selectedPsap, setSelectedPsap] = useState<PsapWithDistance | null>(null);

  const notInvolvedPsaps = notAlreadyInvolvedPsaps(relatedPsaps, involvedPsaps);
  const orderedRelatedPsaps = orderRelatedPsaps(notInvolvedPsaps, lastCallerPosition);
  const noPsapsToAdd = notInvolvedPsaps.length === 0;

  const language = i18n.language || 'en';

  useImperativeHandle(ref, () => ({
    showModal: () => {
      modalRef.current?.show();
    },
  }));

  const closeDialog = () => {
    modalRef.current?.hide();
  };

  const addPsapToIncident = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedPsap != null) {
      await requestCollaboration(incidentId, selectedPsap.psapInfo.id);
      closeDialog();
    }
  };

  return (
    <NgModal
      ref={modalRef}
      header={t('AddIncidentToPsapDialog_AddPsapHeader')}
      bodyTemplate={
        noPsapsToAdd ? (
          <div className="border border-1 border-dark-subtle border-dashed p-3 text-black-50 bg-light small text-center">
            {t('AddIncidentToPsapDialog_AllPsapsAlreadyAdded')}
          </div>
        ) : (
          <form id={FORM_ID} onSubmit={(event) => void addPsapToIncident(event)}>
            <div>
              <NgDataListSelect<PsapWithDistance>
                items={orderedRelatedPsaps}
                value={selectedPsap ?? undefined}
                onValueChanged={(value) => setSelectedPsap(value ?? null)}
                textSelector={(psap) => getPsapDisplayName(psap, language)}
                nullText={t('AddIncidentToPsapDialog_SelectRescueUnitPlaceholderText')}
                label={t('AddIncidentToPsapDialog_SelectRescueUnitToAddLabel')}
              />
            </div>
          </form>
        )
      }
      footerTemplate={
        <>
          {!noPsapsToAdd && (
            <NgButton
              submitButton
              form={FORM_ID}
              icon="fal fa-share-from-square"
              text={t('AddIncidentToPsapDialog_Button_AddRescueUnit')}
              disabled={selectedPsap == null}
            />
          )}
          <NgButton text={t('Button_Close')} outline onClick={closeDialog} />
        </>
      }
    />
  );
}
